/*

  Recentlist.ts

  List of recent games display component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView, RecentListItem } from "./types";
import { ts } from "./i18n";
import { ComponentFunc, VnodeChildren, m } from "./mithril";
import { gameUrl, glyph, glyphGrayed, nbsp } from "./util";
import { ProModeIndicator } from "./components";

interface IAttributes {
  view: IView;
  recentList: RecentListItem[];
  id: string;
}

export const RecentList: ComponentFunc<IAttributes> = () => {
  // Shows a list of recent games, stored in vnode.attrs.recentList

  function itemize(item: RecentListItem, i: number): VnodeChildren {

    // Generate a list item about a recently completed game

    function durationDescription(): VnodeChildren {
      // Format the game duration
      let duration: VnodeChildren = "";
      if (!item.prefs?.duration) {
        // Regular (non-timed) game
        if (item.days || item.hours || item.minutes) {
          if (item.days > 1)
            duration = item.days.toString() + ts(" dagar");
          else
            if (item.days == 1)
              duration = ts("1 dagur");
          if (item.hours > 0) {
            if (duration.length)
              duration += ts(" og ");
            if (item.hours == 1)
              duration += ts("1 klst");
            else
              duration += item.hours.toString() + ts(" klst");
          }
          if (item.days === 0) {
            if (duration.length)
              duration += ts(" og ");
            if (item.minutes == 1)
              duration += ts("1 mínúta");
            else
              duration += item.minutes.toString() + ts(" mínútur");
          }
        }
      }
      else {
        // This was a timed game
        duration = [
          m("span.timed-btn", { title: ts('Viðureign með klukku') }),
          " 2 x " + item.prefs.duration + ts(" mínútur")
        ];
      }
      return duration;
    }

    // Show the Elo point adjustments resulting from the game
    let eloAdj: VnodeChildren = item.elo_adj ? item.elo_adj.toString() : "";
    let eloAdjHuman: VnodeChildren = item.human_elo_adj ? item.human_elo_adj.toString() : "";
    let eloAdjClass = "";
    let eloAdjHumanClass = "";
    // Find out the appropriate class to use depending on the adjustment sign
    if (item.elo_adj !== null) {
      if (item.elo_adj > 0) {
        eloAdj = "+" + eloAdj;
        eloAdjClass = "elo-win";
      }
      else
      if (item.elo_adj < 0)
        eloAdjClass = "elo-loss";
      else {
        eloAdjClass = "elo-neutral";
        eloAdj = glyph("stroller", { title: 'Byrjandi' });
      }
    if (item.human_elo_adj !== null)
      if (item.human_elo_adj > 0) {
        eloAdjHuman = "+" + eloAdjHuman;
        eloAdjHumanClass = "elo-win";
      }
      else
      if (item.human_elo_adj < 0)
        eloAdjHumanClass = "elo-loss";
      else {
        eloAdjHumanClass = "elo-neutral";
        eloAdjHuman = glyph("stroller", { title: 'Byrjandi' });
      }
    }
    eloAdj = m("span",
      { class: 'elo-btn right ' + eloAdjClass + (eloAdj == "" ? " invisible" : "") },
      eloAdj
    );
    eloAdjHuman = m("span",
      { class: 'elo-btn left ' + eloAdjHumanClass + (eloAdjHuman == "" ? " invisible" : "") },
      eloAdjHuman
    );

    return m(".listitem" + (i % 2 === 0 ? ".oddlist" : ".evenlist"),
      m(m.route.Link,
        // Clicking on the link opens up the game
        { href: gameUrl(item.url) },
        [
          m("span.list-win",
            item.sc0 >= item.sc1 ?
              glyph("bookmark", { title: item.sc0 == item.sc1 ? ts("Jafntefli") : ts("Sigur") }) :
              glyphGrayed("bookmark", { title: ts("Tap") })
          ),
          m("span.list-ts-short", item.ts_last_move),
          m("span.list-nick",
            item.opp_is_robot ? [glyph("cog"), nbsp(), item.opp] : item.opp
          ),
          m("span.list-s0", item.sc0),
          m("span.list-colon", ":"),
          m("span.list-s1", item.sc1),
          m("div.list-elo-adj", eloAdjHuman),
          m("div.list-elo-adj", eloAdj),
          m("span.list-duration", durationDescription()),
          m(ProModeIndicator, { manual: item.prefs?.manual })
        ]
      )
    );
  }

  return {
    view: (vnode) => {
      const list = vnode.attrs.recentList;
      return m("div", { id: vnode.attrs.id }, !list ? "" : list.map(itemize));
    }
  };
};
